import React from 'react';
import { DoorOpen } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { useBreakpoint, useLiquidGlass } from '@/hooks';
import { FORM_GLASS_SETTINGS } from '@/constants';
import styles from './HtsQuestionnaire.module.scss';

// Pill-shaped, so clamp the corner radius to a true pill the same way
// MainLayout's header bar does (useLiquidGlass clamps `radius` to the
// element's own half-height, so any value at/above that resolves to a pill).
const EXIT_BTN_GLASS_SETTINGS = { ...FORM_GLASS_SETTINGS, radius: 999 };

interface HtsTopbarProps {
  /** 1-based position of the question currently on screen — independent of how many have actually been answered. */
  currentQuestionNumber: number;
  answeredCount: number;
  total: number;
  onExit: () => void;
}

const ShieldCheckIcon: React.FC = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3l7 3v6c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6z" />
    <path d="M9.5 12.2l1.8 1.8 3.2-3.6" />
  </svg>
);

export const HtsTopbar: React.FC<HtsTopbarProps> = ({ currentQuestionNumber, answeredCount, total, onExit }) => {
  const { isDesktop, isMobile } = useBreakpoint();
  const pct = total > 0 ? Math.round((answeredCount / total) * 100) : 0;
  const exitGlassRef = useLiquidGlass<HTMLButtonElement>(EXIT_BTN_GLASS_SETTINGS);

  return (
    <>
      <header className={styles.topbar}>
        <div className={styles.topbarMain}>
          <div className={styles.topbarBrand}>
            <Logo type="lettertype" mode="light" className={styles.topbarLogo} />
            <span className={styles.topbarDivider} aria-hidden="true" />
            <span className={styles.topbarPageName}>
              {isDesktop ? 'High-Trust Suitability Questionnaire' : 'HTS Questionnaire'}
            </span>
          </div>

          <div className={styles.topbarRight}>
            <span
              className={styles.confidentialBadge}
              title={isMobile ? 'Confidential' : undefined}
              aria-label={isMobile ? 'Confidential' : undefined}
            >
              <ShieldCheckIcon />
              {!isMobile && 'Confidential'}
            </span>
            <button type="button" ref={exitGlassRef} className={styles.exitBtn} onClick={onExit}>
              {!isMobile && <DoorOpen style={{ width: '1em', height: '1em' }} />}
              <span>Exit</span>
            </button>
          </div>
        </div>

        {/* Doubles as the header's own bottom border (the unfilled track
            color) and the progress indicator (the filled portion) — one 3px
            strip, not a border plus a separate bar layered on top of it. */}
        <div
          className={styles.topbarProgressTrack}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${pct}% complete`}
        >
          <div className={styles.topbarProgressFill} style={{ width: `${pct}%` }} />
        </div>
      </header>

      <div className={styles.topbarMeta}>
        <span className={styles.topbarMetaText}>
          {isMobile ? `Q${currentQuestionNumber} of ${total}` : `Question ${currentQuestionNumber} of ${total}`}
        </span>
        <span className={styles.topbarMetaText}>{isMobile ? `${pct}%` : `${pct}% complete`}</span>
      </div>
    </>
  );
};
